import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, storedUsername } from '../../context/AuthContext';
import { buildApiUrl } from '../../config/api';
import { usePreferences } from '../usePreferences';
import { apiErrorMessage } from '../prescriptionForm';
import { needsRecordedMax, validateMaxForm, buildMaxRequest } from '../trainingData';
import { trackingOf, fieldsFor, buildSetRequest, classifyMeasure } from './tracking';
import { classifySetRow, SET_READY, todayLocalISODate } from './setEntry';
import { newClientId } from './clientId';
import * as snapshot from './sessionSnapshot';
import { offlineWeek } from './prefetch';
import { enqueue } from './offlineQueue';
import { getSyncState, subscribeSync, refreshSync, runSync } from './offlineSync';
import {
  startAt, currentGroup, currentStation, currentTarget, setPosition, restPreview,
  advance, totalSteps, progressDone, restAfter, tempoPhases, tempoEmphasis, stationRestPreview, groupEquipment,
} from './execution';
import RestTimer, { formatClock } from './RestTimer';
import SessionClock from './SessionClock';
import { expectedWorkSeconds, fellShortOfPrescribed } from './setTiming';
import ExerciseVideoModal from '../ExerciseVideoModal';
import { exerciseVideoLink } from '../video';
import '../../styles/ExecutionLogger.css';

// Training a session, one set at a time.
//
// # WHY NOT A LIST OF EXERCISES WITH BOXES
//
// Because that is not how this style of training is performed. A1 and A2 are
// alternated, the 10 seconds after A1 is a walk to the next station and the 120
// after A2 is the recovery that makes the next set possible — none of which a
// flat list of cards can express. The athlete is somewhere in a circuit, and the
// only question this screen answers is "what is in front of me".
//
// The server decides what the circuits are (routes/execution.go); this decides
// where the athlete is in one.
//
// # THE DEMO
//
// ExecutionStation has carried YouTubeLink since it was written
// (routes/execution.go), and for a long time nothing here read it: the screen
// where an athlete is actually performing the lift was the one screen with no
// way to see it performed. The editor and the flat logger both had a Demo
// button; this, the screen the "Start training" button leads to, did not.
//
// The tempo is shown broken into its phases because "4-0-1-0" is a prescription
// and reading it as a label means lifting it in one second, which is a different
// exercise. This displays it; it does not count it out — a metronome is a
// separate feature with its own audio and vibration questions.

// No response at all (offline, DNS, a dropped connection) or the server failing
// rather than answering. Either way the set was not refused, it just could not
// be heard, so it is kept for later. A 4xx is the server's answer about the set
// itself and is shown instead.
function isUnreachable(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

const ExecutionLogger = () => {
  const { programId, sessionId } = useParams();
  const { token, username } = useAuth();
  const { unit, loadRange } = usePreferences();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [resting, setResting] = useState(null);
  const [row, setRow] = useState({});

  // The key for the set in front of the athlete, kept across retries of it.
  //
  // A set that saved but whose reply was lost comes back as "That set did not
  // save", and the athlete taps again. With a new key per tap that retap was a
  // second copy of the set in their history. With the same key the server
  // answers it with the set it already has (PERF-13).
  //
  // Made on the first tap, and forgotten whenever the set stops being the same
  // set: a new position in the circuit, different numbers typed, or a different
  // week.
  const setKey = useRef(null);
  // The demo being watched, or null.
  const [demo, setDemo] = useState(null);
  // 1 until the server says otherwise. That fallback is correct ONLY when there
  // is no schedule to derive a week from — an athlete training a block nobody
  // assigned. For anybody in a real block it is the wrong answer, and it used to
  // be the only answer: every set filed into week 1 regardless of the week they
  // were actually in, which breaks the grid, the progression trend and adherence
  // at once without saying anything.
  const [weekNumber, setWeekNumber] = useState(1);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [saving, setSaving] = useState(false);
  // The inline record-a-max control, for a %1RM set with no max yet.
  const [maxOpen, setMaxOpen] = useState(false);
  const [maxValue, setMaxValue] = useState('');
  const [savingMax, setSavingMax] = useState(false);
  // When the session came from the phone's saved copy rather than the server,
  // the time that copy was saved. The logger says so: a %1RM in it resolved
  // against the max the athlete had THEN.
  const [offlineCopy, setOfflineCopy] = useState(null);
  // A saved copy from before this week began whose week could not be worked
  // out offline (prefetch.offlineWeek): the athlete is asked to check it.
  const [weekUncertain, setWeekUncertain] = useState(false);
  const [sync, setSync] = useState(getSyncState);

  // When this session began, for SessionClock — null until the athlete
  // presses Start on the very first series' pre-flight gate (see
  // startedThroughGroup below). The session has not begun at page load; it
  // begins when the athlete says so, not when the page happened to finish
  // loading.
  const sessionStartedAt = useRef(null);
  // When the set now in front of the athlete became the active one. Reset in
  // goNext, which runs exactly when a new set takes the cursor's place —
  // ending a rest, carrying straight on with no rest, or skipping a set. Also
  // reset, unconditionally, by startSeries — the guard that actually matters
  // for a gated series: whatever goNext set this to while a pre-flight gate
  // was showing, nothing could have logged a set yet to read it, so
  // startSeries's own reset is what keeps the athlete's setup time off the
  // new series' first set.
  const workStartedAt = useRef(Date.now());
  // When the athlete FIRST pressed Log set for the set in front of them, or
  // null. A refused save keeps them on the same set to correct what they
  // typed, and the retry must not move the end of the set: recomputing it per
  // press counted the time spent resting and correcting as work (a 10s set
  // saved as 40s) and restarted the rest from the retry. Cleared wherever
  // workStartedAt is reset, because that is where a different set begins.
  const setFinishedAt = useRef(null);
  // The highest group index the athlete has pressed Start Series on. Groups
  // are only ever walked forward (advance() never decrements `group`), so
  // this one monotonic number is enough to know whether the group the cursor
  // now points at has been started — no need for a set of visited indices.
  // -1 means no series has been started yet, which gates group 0 too.
  const [startedThroughGroup, setStartedThroughGroup] = useState(-1);
  // The rest currently running: { startedAt, prescribedSeconds }, so endRest
  // can measure it against what was prescribed. Set in carryOn, consumed in
  // endRest.
  const restRunning = useRef(null);
  // The settled rest measurement (seconds) to attach to the NEXT set logged,
  // or null. Set by endRest once a rest actually runs its course, read (not
  // cleared) when that set is logged, and cleared in carryOn once the set
  // genuinely advances — not on a failed save, so a retry of the same set
  // still carries the rest that preceded it.
  const pendingRest = useRef(null);
  // What to say about the set/rest that just happened, shown once on the
  // screen that follows. Factual, never blocking — see setTiming.js for why
  // this only ever fires on RUSHED, never on taking longer.
  const [pacingNotes, setPacingNotes] = useState([]);
  // A running tally for the session-complete screen, from the exact same
  // fellShortOfPrescribed calls that drive pacingNotes above — not a second
  // computation. `onPace` is how many of `measurable` were NOT flagged. A set
  // with no tempo is in neither work count — see session-adherence-summary.md's
  // sibling principle for equipment: null means not measured, not measured-
  // and-fine. The rest side never has a null case to guard against: carryOn
  // only starts a rest running when it has a real positive prescribed
  // duration (no rest is prescribed, or this is the session's last set, both
  // mean no RestTimer and no endRest call at all) — see endRest's own comment.
  const [adherence, setAdherence] = useState({
    workMeasurable: 0, workOnPace: 0, restMeasurable: 0, restOnPace: 0,
  });

  useEffect(() => subscribeSync(setSync), []);
  useEffect(() => { refreshSync(username); }, [username]);

  const authConfig = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const fetchTrain = useCallback(async () => {
    const { data } = await axios.get(
      buildApiUrl(`/programs/${programId}/sessions/${sessionId}/train`),
      authConfig()
    );
    return data;
  }, [programId, sessionId, authConfig]);

  useEffect(() => {
    const start = (data) => {
      setSession(data);
      setCursor(startAt(data.groups));

      // 0 means the server had no schedule to derive a week from, which is a
      // real state — not a failure, and not week 1 dressed up as one.
      if (data.currentWeek > 0) {
        setWeekNumber(data.currentWeek);
      }
    };

    (async () => {
      let data;
      try {
        data = await fetchTrain();
      } catch (err) {
        // No network (or no server): open the copy saved the last time this
        // athlete opened this session, if there is one. A refusal (404, 403) is
        // an answer, not an outage, and is reported as before.
        const saved = isUnreachable(err) ? snapshot.load(username, programId, sessionId) : null;
        if (saved) {
          // The copy's currentWeek is the server's answer for the day it was
          // saved, which for a prefetched copy may be last week (prefetch.js).
          // The week selector, from the phase list saved with it, so a wrong
          // week can still be corrected with no signal.
          const phases = saved.data.phaseId ? snapshot.loadPhases(username, programId)?.data : null;
          const savedPhase = (phases ?? []).find((p) => p.id === saved.data.phaseId) ?? null;
          const { week, certain } = offlineWeek(saved);
          start({ ...saved.data, currentWeek: week });
          setWeekUncertain(!certain);
          setOfflineCopy(saved.savedAt);
          setPhase(savedPhase);
        } else {
          setNotice({ tone: 'error', message: apiErrorMessage(err, 'Could not load this session.') });
        }
        setLoading(false);
        return;
      }

      // Copies are written only while the athlete who asked is still signed in.
      // Logout clears this user's snapshot prefix synchronously; a response
      // landing after it must not write their programme back onto the phone.
      const stillMine = () => storedUsername() === username;

      try {
        start(data);
        if (stillMine()) snapshot.save(username, programId, sessionId, data);

        if (data.phaseId) {
          try {
            const res = await axios.get(
              buildApiUrl(`/programs/${programId}/phases`), authConfig());
            const phases = res.data?.phases ?? [];
            if (stillMine()) snapshot.savePhases(username, programId, phases);
            setPhase(phases.find((p) => p.id === data.phaseId) ?? null);
          } catch { /* the week selector is an affordance, not a precondition */ }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchTrain, programId, sessionId, username, authConfig]);

  // Reload the session's prescriptions WITHOUT moving the athlete. The initial
  // effect resets the cursor to the start; this deliberately does not — after
  // recording a max, the athlete must stay on the set they were about to lift,
  // now with its load resolved. The group structure is unchanged (only
  // percentLoad fills in), so the current cursor stays valid.
  const refetchSession = useCallback(async () => {
    setSession(await fetchTrain());
  }, [fetchTrain]);

  // Memoised so the fallback [] is not a NEW array on every render, which would
  // make every useMemo below it recompute every time and defeat the point.
  const groups = useMemo(() => session?.groups ?? [], [session]);
  const group = currentGroup(groups, cursor);
  // A pre-flight gate is owed whenever the cursor points into a series the
  // athlete has not pressed Start on yet — see startedThroughGroup above.
  const seriesGateActive = Boolean(cursor) && cursor.group > startedThroughGroup;
  // Combined across every station in the group, deduplicated — series-
  // equipment-checklist.md. Only read while the gate itself is active; a
  // bodyweight-only series (or any series once it's already started) has no
  // use for this, so it's gated the same as everything else that's gate-only.
  const gateEquipment = useMemo(
    () => (seriesGateActive ? groupEquipment(group) : []),
    [seriesGateActive, group]
  );
  const station = currentStation(groups, cursor);
  const target = currentTarget(groups, cursor);
  const position = setPosition(groups, cursor);
  const tracking = trackingOf(station);
  // Absent for most of the corpus, which is a state and not a fault: no link,
  // no control. See components/video.js — a "0" placeholder is absent too.
  const demoLink = exerciseVideoLink(station);
  const fields = fieldsFor(tracking);
  const phases = useMemo(() => tempoPhases(station?.tempo), [station]);
  const emphasis = useMemo(() => tempoEmphasis(station?.tempo), [station]);
  // The rest that FOLLOWS this set, shown before it is taken.
  const restPlan = useMemo(() => restPreview(groups, cursor), [groups, cursor]);

  // Counts the set just logged while its rest is running (progressDone).
  const done = progressDone(groups, cursor, resting);
  const total = totalSteps(groups);

  // Every keystroke makes a new row object, and advancing makes a new cursor,
  // so this runs whenever the set stops being the one the key was made for. A
  // failed save changes none of them, which is what keeps the key for the retry.
  useEffect(() => {
    setKey.current = null;
  }, [row, cursor, weekNumber]);

  const logSet = async () => {
    if (!station) return;
    if (!setKey.current) setKey.current = newClientId();
    const clientId = setKey.current;

    // How long this set actually took, and the rest that preceded it — read
    // now, while they still describe THIS set, not whatever set comes next.
    //
    // loggedAt is also when the next REST starts: the athlete stopped working
    // when they pressed Log set, not when the save came back. Starting the rest
    // clock after the POST lost the whole wait on a slow connection — a 60s rest
    // spent 40s waiting on the network read as 20s, with a "rest was short" note.
    //
    // A retry after a refused save reuses the first press — see setFinishedAt.
    if (setFinishedAt.current === null) setFinishedAt.current = Date.now();
    const loggedAt = setFinishedAt.current;
    const workSeconds = Math.round((loggedAt - workStartedAt.current) / 1000);
    const restSeconds = pendingRest.current;

    let body;
    if (fields.duration || fields.distance) {
      const field = fields.duration ? 'duration' : 'distance';
      const measure = classifyMeasure(row[field], tracking);
      if (measure.status !== 'ready') {
        setNotice({ tone: 'error', message: measure.reason || `Enter the ${field}.` });
        return;
      }
      body = buildSetRequest({
        tracking, [field]: measure.value, unit,
        setNumber: cursor.set, weekNumber, sessionDate: todayLocalISODate(), clientId,
        workSeconds, restSeconds,
      });
    } else {
      // The weight path goes through classifySetRow, which carries the
      // bodyweight fix: a blank load means bodyweight, not "skip".
      const result = classifySetRow(row, loadRange);
      if (result.status !== SET_READY) {
        setNotice({
          tone: 'error',
          message: result.reason || 'Enter the reps for this set.',
        });
        return;
      }
      body = buildSetRequest({
        tracking, weight: result.weight, reps: result.reps, unit,
        setNumber: cursor.set, weekNumber, sessionDate: todayLocalISODate(), clientId,
        workSeconds, restSeconds,
      });
    }

    // Relative to the API base: it is replayed later through buildApiUrl, so a
    // queued set does not carry a hostname.
    const path = `/programs/${programId}/sessions/${sessionId}/exercises/${station.entryId}/performances`;

    // Whether the set went to the server or onto the phone, the athlete's next
    // step is the same: the rest, then the next set.
    const carryOn = () => {
      // A fresh note for THIS transition, replacing whatever was said about
      // the one before it — not accumulated, or a note from two sets ago
      // would still be on screen. endRest (below) appends the rest half of
      // the story once that rest actually ends; until then this is the whole
      // of it.
      const notes = [];
      const expectedWork = expectedWorkSeconds(station.tempo, target);
      const workShort = fellShortOfPrescribed(expectedWork, workSeconds);
      if (workShort) {
        notes.push(`That set moved quickly for a ${station.tempo} tempo.`);
      }
      if (workShort !== null) {
        setAdherence((a) => ({
          ...a,
          workMeasurable: a.workMeasurable + 1,
          workOnPace: a.workOnPace + (workShort ? 0 : 1),
        }));
      }
      setPacingNotes(notes);
      // Consumed: this request already carried it. Left alone on a failed
      // save (this function is never reached then), so a retry of the same
      // set still describes the rest that actually preceded it.
      pendingRest.current = null;

      const rest = restAfter(groups, cursor);
      setRow({});
      if (rest && rest.role !== 'done' && rest.seconds > 0) {
        restRunning.current = { startedAt: loggedAt, prescribedSeconds: rest.seconds };
        // The countdown starts from the same instant as the measurement, so the
        // screen does not show a full rest the save already spent part of.
        setResting({ ...rest, startedAt: loggedAt });
      } else {
        goNext();
      }
    };

    setSaving(true);
    setNotice(null);
    try {
      await axios.post(buildApiUrl(path), body, authConfig());
      carryOn();
      // The network is up: send anything waiting from earlier.
      runSync({ user: username, token });
    } catch (err) {
      if (isUnreachable(err)) {
        // Kept on the phone with the body exactly as sent, clientId included,
        // so the replay is the same set and never a second copy (PERF-13).
        enqueue({
          user: username,
          path,
          body,
          label: `${station.series} ${station.exerciseName} · set ${cursor.set}`,
        });
        refreshSync(username);
        setNotice({ tone: 'success', message: "Saved on this phone. It will sync when you're back online." });
        carryOn();
      } else {
        // The server read the set and refused it: retrying will not help, and
        // the athlete can fix it now. Stay on the set.
        setNotice({ tone: 'error', message: apiErrorMessage(err, 'That set did not save.') });
      }
    } finally {
      setSaving(false);
    }
  };

  // Record the missing max for the exercise in front of the athlete, then reload
  // so the server resolves the %1RM into a load. The exercise is known — this is
  // the set they are on — so unlike MyTraining's form there is no picker, only a
  // value. The number is stored against the athlete, in their unit; the server
  // converts and resolves (routes/maxes.go, routes/programs.go).
  const recordMax = async () => {
    if (!station) return;
    const form = { exerciseName: station.exerciseName, value: maxValue };
    const errors = validateMaxForm(form);
    if (errors.value || errors.exerciseName) {
      setNotice({ tone: 'error', message: errors.value || errors.exerciseName });
      return;
    }

    setSavingMax(true);
    setNotice(null);
    try {
      await axios.put(buildApiUrl('/maxes'), buildMaxRequest(form, unit), authConfig());
      await refetchSession();
      setMaxOpen(false);
      setMaxValue('');
      setNotice({ tone: 'success', message: 'Max recorded — your load is updated.' });
    } catch (err) {
      setNotice({
        tone: 'error',
        message: isUnreachable(err)
          ? 'Recording a max needs a connection. Your sets still save on this phone.'
          : apiErrorMessage(err, 'That max did not save.'),
      });
    } finally {
      setSavingMax(false);
    }
  };

  const goNext = () => {
    setResting(null);
    const next = advance(groups, cursor);
    // A new set takes the cursor's place now, whatever got us here — ending a
    // rest, carrying straight on with none, or skipping. Its own clock starts
    // here, not when the PREVIOUS set's work began. Harmless when this lands
    // on a not-yet-started series' gate too: startSeries unconditionally
    // resets this again the moment Start is pressed, and nothing can read it
    // before then — there is no Log Set button to press while gated.
    workStartedAt.current = Date.now();
    setFinishedAt.current = null;
    setCursor(next);
    setRow({});
    // A different set may not be a %1RM, and a stale open form on it would be
    // wrong; reset the control as the athlete moves.
    setMaxOpen(false);
    setMaxValue('');
  };

  // Called from the pre-flight gate's Start Series button. workStartedAt is
  // reset here UNCONDITIONALLY — not only when goNext's own reset missed —
  // because it is the single guard this screen relies on to keep an
  // athlete's setup time off the new series' first set: whatever goNext set
  // it to while the gate was showing, nothing could have read it yet.
  // sessionStartedAt is set only once, on the very first press: the session
  // begins when the athlete says so.
  const startSeries = () => {
    if (!cursor) return;
    setStartedThroughGroup(cursor.group);
    workStartedAt.current = Date.now();
    setFinishedAt.current = null;
    if (sessionStartedAt.current === null) {
      sessionStartedAt.current = Date.now();
    }
  };

  // Ends the rest RestTimer is showing: measures it against what was
  // prescribed, before goNext moves the cursor and the prescription that
  // measurement describes is gone.
  const endRest = () => {
    const running = restRunning.current;
    restRunning.current = null;
    if (running) {
      const seconds = Math.round((Date.now() - running.startedAt) / 1000);
      pendingRest.current = seconds;
      // No null guard needed here, unlike carryOn's work-side tally below:
      // restRunning is only ever set (in carryOn) when rest.seconds > 0, so
      // running.prescribedSeconds is always positive by the time endRest
      // runs at all — fellShortOfPrescribed can never return null here.
      // Mutation-tested: a `!== null` guard added back in produced no
      // observable difference, because this branch is simply never reached
      // with a null result.
      const restShort = fellShortOfPrescribed(running.prescribedSeconds, seconds);
      if (restShort) {
        setPacingNotes((notes) => [
          ...notes,
          `Rest was ${formatClock(seconds)}, prescribed ${formatClock(running.prescribedSeconds)}.`,
        ]);
      }
      setAdherence((a) => ({
        ...a,
        restMeasurable: a.restMeasurable + 1,
        restOnPace: a.restOnPace + (restShort ? 0 : 1),
      }));
    }
    goNext();
  };

  // Skipping a set, not its rest — there is no rest measurement to attach to
  // whatever comes next, and keeping one around would misattribute it to a
  // different set than the one it was actually measured before.
  const skipSet = () => {
    pendingRest.current = null;
    goNext();
  };

  // Sets from THIS session waiting on the phone. Distinct from done: a set
  // waiting to sync is not yet in the athlete's history, and saying it is would
  // be the same lie as "0 of 8 sets logged" in the other direction.
  const sessionPrefix = `/programs/${programId}/sessions/${sessionId}/`;
  const sessionPending = sync.user === username
    ? sync.pending.filter((item) => item.path.startsWith(sessionPrefix)).length
    : 0;

  // How many weeks the selector offers: the phase's block length when the
  // phase list is known. On an offline copy whose week is uncertain, the
  // athlete is asked to check it, so a choice must exist even when no phase
  // list was saved (a copy from before they were): the block length the server
  // sent with the copy, or the 1-26 a week can be (WEEK-3).
  let weeksOffered = 0;
  if (phase?.blockLengthWeeks > 1) weeksOffered = phase.blockLengthWeeks;
  else if (weekUncertain) weeksOffered = session?.weekBasis?.blockLengthWeeks || 26;

  if (loading) {
    return <div className="logger"><p className="logger-status">Loading…</p></div>;
  }

  // A session that never loaded is not a finished one. Without this, a failed
  // load fell through to the summary below, "Session complete · 0 of 0 sets
  // logged", with the error that explained it never rendered. Offline with no
  // saved copy, that is what opening a session looked like.
  if (!session) {
    return (
      <div className="logger">
        <div className="logger-done">
          <h1>This session could not open</h1>
          <p className="status-error" role="alert">
            {notice?.message || 'Could not load this session.'}
          </p>
          <Link className="btn-primary" to="/training">Back to your training</Link>
        </div>
      </div>
    );
  }

  if (!cursor || !station) {
    return (
      <div className="logger">
        <div className="logger-done">
          <h1>Session complete</h1>
          <p className="meta-copy">
            {sessionPending > 0
              ? `${done} of ${total} sets done · ${sessionPending} waiting to sync.`
              : `${done} of ${total} sets logged.`}
          </p>
          {/* Quiet, factual, same register as the live pacing notes — never a
              score, never praise, and silent when nothing was measurable
              (see adherence's own comment for what counts). */}
          {adherence.workMeasurable > 0 && (
            <p className="meta-copy logger-adherence">
              {adherence.workOnPace} of {adherence.workMeasurable} measurable sets kept pace with their tempo.
            </p>
          )}
          {adherence.restMeasurable > 0 && (
            <p className="meta-copy logger-adherence">
              {adherence.restOnPace} of {adherence.restMeasurable} measurable rests held the full prescribed time.
            </p>
          )}
          <Link className="btn-primary" to="/training">Back to your training</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="logger">
      <header className="logger-bar">
        <button type="button" className="logger-exit" onClick={() => navigate('/training')}>
          ✕
        </button>
        {/* role="progressbar", because aria-label on a plain div is prohibited —
            it has no role for the name to attach to, so assistive technology was
            given a label for nothing. The value attributes are what make it
            answer "how far through am I", which is the question it draws. */}
        <div
          className="logger-progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total}
          aria-valuenow={done}
          aria-label={`Set ${done + 1} of ${total}`}
        >
          <div className="logger-progress-fill" style={{ width: `${(done / total) * 100}%` }} />
        </div>
        <span className="logger-count mono">{done}/{total}</span>
        {/* No session to clock until the athlete presses Start on the first
            series' gate — see sessionStartedAt above. Same markup/class as
            the real clock so there is no visual jump the moment it mounts. */}
        {sessionStartedAt.current !== null ? (
          <SessionClock startedAt={sessionStartedAt.current} running={!seriesGateActive} />
        ) : (
          <span className="session-clock mono">0:00</span>
        )}
        {sessionPending > 0 && (
          <span className="logger-sync" role="status">{sessionPending} to sync</span>
        )}
      </header>

      {offlineCopy && (
        <p className="logger-offline meta-copy" role="status">
          Offline: using the copy saved {snapshot.describeSavedAt(offlineCopy)}.
          {weekUncertain && ' It was saved before this week began, so its week may be out of date: check it before you log.'}
        </p>
      )}

      {weeksOffered > 1 && (
        <div className="logger-week">
          <label htmlFor="logger-week">Week</label>
          <select
            id="logger-week"
            value={weekNumber}
            onChange={(e) => setWeekNumber(Number(e.target.value))}
          >
            {Array.from({ length: weeksOffered }, (_, i) => i + 1).map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </div>
      )}

      {resting ? (
        <RestTimer
          seconds={resting.seconds}
          role={resting.role}
          nextLabel={resting.nextLabel}
          startedAt={resting.startedAt}
          onSkip={endRest}
        />
      ) : seriesGateActive ? (
        // Every series — including the first — opens here, not on a set.
        // Nothing times while this is on screen: SessionClock is paused
        // (running={!seriesGateActive} above) and workStartedAt is not reset
        // until startSeries runs, so setup time is never counted as training
        // time. No skip path, by design — see series-preflight-gate.md.
        <main className="logger-gate">
          <p className="kicker logger-gate-kicker">Series {group.series}</p>
          <h1 className="logger-gate-title">Get set up</h1>
          <ul className="logger-gate-stations">
            {group.stations.map((s) => {
              const rest = stationRestPreview(s);
              const stationPhases = tempoPhases(s.tempo);
              return (
                <li key={s.entryId} className="logger-gate-station">
                  <span className="logger-gate-series mono">{s.series}</span>
                  <span className="logger-gate-exercise">{s.exerciseName}</span>
                  {stationPhases && <span className="mono">{s.tempo}</span>}
                  <span className="mono" aria-label={`Rest ${rest.spoken}`}>
                    {rest.value} {rest.label}
                  </span>
                </li>
              );
            })}
          </ul>
          {/* One combined line, not one per station — an athlete preparing
              for a superset grabs everything once. Absent entirely for a
              bodyweight-only series: nothing to fetch, nothing shown. See
              series-equipment-checklist.md. */}
          {gateEquipment.length > 0 && (
            <p className="logger-gate-equipment">
              You&rsquo;ll need: {gateEquipment.join(', ')}
            </p>
          )}
          <button
            type="button"
            className="btn-primary logger-gate-start"
            onClick={startSeries}
          >
            Start Series {group.series}
          </button>
        </main>
      ) : (
        <main className="logger-set">
          {/* Factual, never blocking — see setTiming.js for why this only
              ever fires on moving faster than prescribed, never slower or
              longer. Empty most of the time: no tempo prescribed, or the set
              matched its pace, are both silence, not a hidden "good job". */}
          {pacingNotes.length > 0 && (
            <div className="logger-pacing" role="status">
              {pacingNotes.map((note, i) => <p key={i}>{note}</p>)}
            </div>
          )}

          <div className="logger-where">
            <span className="logger-series mono">{station.series}</span>
            {/* One label, not two. A superset used to say "Round 2 of 3" and
                straight sets "Set 2 of 3" — the same thing under two names, and
                the circuit one described the walk rather than the work. What an
                athlete is doing is a set of THIS exercise either way.

                The denominator comes from setPosition, not group.rounds: see
                that function for why "3 of 5" was a promise A2 could not keep. */}
            <span className="logger-set-of">
              Set {position.index} of {position.total}
            </span>
            {demoLink && (
              <button
                type="button"
                className="btn-ghost logger-demo"
                onClick={() => setDemo({ url: demoLink, name: station.exerciseName })}
              >
                Demo
              </button>
            )}
          </div>

          <h1 className="logger-exercise">{station.exerciseName}</h1>

          <div className="logger-target">
            <span className="logger-target-value mono">{target || '—'}</span>
            <span className="logger-target-label">
              {fields.duration ? 'seconds' : fields.distance ? 'metres' : 'reps'}
            </span>
            {station.percentLoad != null && (
              <span className="logger-target-load">
                @ {station.percentLoad} {station.unit}
                <span className="muted"> ({station.percentOneRM}% 1RM)</span>
              </span>
            )}
            {/* An absolute load, which STILL WINS when a percentage cannot be
                resolved. LoadKg and PercentOneRM are not mutually exclusive —
                only RPE and RIR are — so a prescription can carry both, and the
                kilograms are the number the athlete can actually put on the bar.
                An earlier version of this block gated the load off whenever a
                percentage was present, which hid the only usable figure behind
                one that resolved to nothing. */}
            {station.percentLoad == null && station.load != null && (
              <span className="logger-target-load">
                @ {station.load} {station.unit}
                {station.percentOneRM != null && (
                  <span className="muted"> ({station.percentOneRM}% 1RM, no max recorded)</span>
                )}
              </span>
            )}
            {/* A percentage with no max and no load behind it still has to be
                SHOWN. It was falling through every branch and rendering nothing,
                so an athlete with no tested max saw a prescription that looked as
                though nobody had said how hard to go — the exact state
                models/max.go calls the pre-Phase-8 bug: storable, and it resolved
                to nothing. It is also the day-one state of every generated block,
                where the athlete has no maxes yet. */}
            {station.percentLoad == null && station.load == null && station.percentOneRM != null && (
              <span className="logger-target-load">
                @ {station.percentOneRM}% 1RM
                <span className="muted"> (no max recorded)</span>
              </span>
            )}
            {station.rpe != null && <span className="logger-target-load">@ RPE {station.rpe}</span>}
            {/* RIR reached the client and was never rendered either — the same
                defect as the percentage, in the same block. */}
            {station.rir != null && <span className="logger-target-load">@ {station.rir} RIR</span>}
          </div>

          {/* The dead end this screen used to be: a %1RM with no recorded max
              said "no max recorded" and offered nothing — the athlete had to
              leave the workout for My Training, guess that maxes live there, and
              come back. The remedy belongs where the refusal is shown. Recording
              it here reloads the prescription so the server resolves the load,
              and leaves the athlete on the same set (refetchSession). */}
          {needsRecordedMax(station) && (
            <div className="logger-record-max">
              {maxOpen ? (
                <>
                  <label className="logger-field logger-record-max-field">
                    <span>Your best {station.exerciseName} ({unit})</span>
                    <input
                      type="number" inputMode="decimal" step="any" autoFocus
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                    />
                  </label>
                  {/* Where the number came from, said plainly.
                      A figure that appears in a box by itself is one the athlete
                      has to either trust blindly or clear — and this one is an
                      inference off their own sets, not something they told us.
                      Saying so is what makes it editable in good conscience: it
                      reads as a starting point, which is what it is. */}
                  {station.suggestedMax != null && (
                    <p className="meta-copy logger-record-max-hint">
                      Estimated from your logged sets. Change it if you know better.
                    </p>
                  )}
                  <div className="logger-record-max-actions">
                    <button
                      type="button" className="btn-primary logger-record-max-save"
                      onClick={recordMax} disabled={savingMax}
                    >
                      {savingMax ? 'Saving…' : 'Save max'}
                    </button>
                    <button
                      type="button" className="btn-ghost"
                      onClick={() => { setMaxOpen(false); setMaxValue(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <button
                  type="button" className="btn-ghost logger-record-max-open"
                  onClick={() => {
                    // Open with the estimate already in it. The empty box was
                    // the dead end: "what is your one-rep max" is exactly the
                    // question somebody who does not know what to do in the gym
                    // cannot answer, and a cursor blinking in nothing is not an
                    // improvement on "no max recorded". Their own sets answer
                    // it — see routes/max_suggestion.go.
                    setMaxValue(station.suggestedMax != null ? String(station.suggestedMax) : '');
                    setMaxOpen(true);
                  }}
                >
                  {station.suggestedMax != null
                    ? `Use about ${station.suggestedMax} ${unit} as your max?`
                    : 'Record your max to see your load'}
                </button>
              )}
            </div>
          )}

          {/* Tempo and rest, side by side and weighted the same.
              Rest used to appear ONLY after the set was logged, in the timer.
              So while deciding how to attack a set an athlete could see how many
              and how fast, and had no idea whether what followed was a ten-second
              walk to a partner exercise or three minutes on the floor — which is
              the difference between a superset and a strength single, and it
              changes how hard you go NOW. It is prescribed, so it is shown. */}
          <div className="logger-prescription">
            {phases && (
              <div className="logger-tempo" aria-label={`Tempo ${station.tempo}`}>
                {phases.map((p, i) => (
                  <span key={i} className="logger-tempo-phase">
                    <span className="logger-tempo-seconds mono">{p.seconds}</span>
                    <span className="logger-tempo-label">{p.label}</span>
                  </span>
                ))}
              </div>
            )}

            <div className="logger-rest" aria-label={`Rest ${restPlan.spoken}`}>
              <span className="logger-rest-value mono">{restPlan.value}</span>
              <span className="logger-rest-label">{restPlan.label}</span>
            </div>
          </div>
          {emphasis && <p className="logger-emphasis">{emphasis}</p>}

          {station.notes && <p className="logger-notes">{station.notes}</p>}

          <div className="logger-inputs">
            {fields.load && (
              <label className="logger-field">
                <span>Load ({unit})</span>
                <input
                  type="number" inputMode="decimal" step="any" placeholder="body"
                  value={row.weight ?? ''}
                  onChange={(e) => setRow({ ...row, weight: e.target.value })}
                />
              </label>
            )}
            {fields.reps && (
              <label className="logger-field">
                <span>Reps</span>
                <input
                  type="number" inputMode="numeric" placeholder={target}
                  value={row.reps ?? ''}
                  onChange={(e) => setRow({ ...row, reps: e.target.value })}
                />
              </label>
            )}
            {fields.duration && (
              <label className="logger-field">
                <span>Seconds</span>
                <input
                  type="number" inputMode="numeric" placeholder={target}
                  value={row.duration ?? ''}
                  onChange={(e) => setRow({ ...row, duration: e.target.value })}
                />
              </label>
            )}
            {fields.distance && (
              <label className="logger-field">
                <span>Metres</span>
                <input
                  type="number" inputMode="decimal" step="any" placeholder={target}
                  value={row.distance ?? ''}
                  onChange={(e) => setRow({ ...row, distance: e.target.value })}
                />
              </label>
            )}
          </div>

          {notice && (
            <p
              className={`${notice.tone === 'success' ? 'status-success' : 'status-error'} logger-notice`}
              role={notice.tone === 'success' ? 'status' : 'alert'}
            >
              {notice.message}
            </p>
          )}

          <button
            type="button"
            className="btn-primary logger-log"
            onClick={logSet}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Log set'}
          </button>

          <button type="button" className="logger-skip" onClick={skipSet}>
            Skip this set
          </button>
        </main>
      )}

      {demo && (
        <ExerciseVideoModal
          videoUrl={demo.url}
          title={demo.name}
          onClose={() => setDemo(null)}
        />
      )}
    </div>
  );
};

export default ExecutionLogger;
